import { FileText, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal, ModalFooter } from './Modal';
import type { ParsedItem } from '../../types';

interface ImportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onConfirm: () => void;
  items: ParsedItem[];
  isPending: boolean;
}

export function ImportPreviewModal({
  isOpen,
  onClose,
  onBack,
  onConfirm,
  items,
  isPending,
}: ImportPreviewModalProps) {
  const { t } = useLanguage();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('checklists.importPreview', 'Import Preview')} (${items.length} ${t('checklists.items', 'items')})`}
      icon={FileText}
      iconColor="primary"
      size="xl"
    >
      <div className="space-y-4">
        {/* Table with items */}
        <div className="border border-gray-200 rounded-xl overflow-hidden max-h-[50vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  {t('checklists.code', 'Code')}
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  {t('checklists.productName', 'Name')}
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">
                  {t('checklists.qty', 'Qty')}
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">
                  {t('checklists.price', 'Price')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, index) => (
                <tr
                  key={`${item.product_code}-${index}`}
                  className="hover:bg-gray-50 transition-colors animate-fade-in"
                  style={{ animationDelay: `${index * 20}ms` }}
                >
                  <td className="px-4 py-3 font-mono text-gray-600">
                    {item.product_code}
                  </td>
                  <td className="px-4 py-3 text-gray-900">
                    {item.item_name}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {item.quantity_needed}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {item.estimated_price ? `$${item.estimated_price.toFixed(2)}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Total estimate if prices exist */}
        {items.some(i => i.estimated_price) && (
          <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
            <span className="text-sm font-medium text-gray-700">
              {t('checklists.estimatedTotal', 'Estimated Total')}
            </span>
            <span className="text-lg font-bold text-gray-900">
              ${items.reduce((sum, i) => sum + (i.estimated_price || 0) * i.quantity_needed, 0).toFixed(2)}
            </span>
          </div>
        )}

        <ModalFooter>
          <button
            onClick={onBack}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            {t('common.back', 'Back')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('checklists.importItems', 'Import Items')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
